/**
 * useKPIData — All data fetching + processing + derived computations.
 *
 * Manages:
 *  - Dropdown lists (states, regions, categories, KPIs)
 *  - KPI data fetching, caching, post-processing
 *  - Dynamic filter config generation
 *  - Aggregated state/region data
 *  - Infrastructure pivot, enrollment special handling, etc.
 */

import { useEffect, useState, useMemo } from 'react';
import {
  getAllCategories,
  getTotalSchools,
  getAllRegions,
  getAllStates,
  getKPIsByCategory,
  getKPIData,
  getAllSchoolCoordinates,
} from '../services/api';
import {
  isInfrastructureCategory,
  INFRA_KPI_OPTIONS,
  pivotInfraData,
  getInfraDefaultColumns,
  INFRA_COLUMN_LABEL_OVERRIDES,
} from '../utils/infraUtils';
import { getCategoryConfig } from '../categories';
import type { CategoryConfig } from '../categories';
import { applyFilters } from '../utils/filterUtils';
import {
  SchoolRecord,
  AggregatedStateData,
  AggregatedRegionData,
} from '../utils/dataAggregation';
import { FilterCondition, FilterConfig } from '../components/AdvancedFilter';
import {
  KPIDataItem,
  StateListItem,
  RegionListItem,
  CategoryListItem,
} from '../types';
import {
  OWNERSHIP_TYPE,
  LOCATION_TYPE,
  SPONSORED_BY,
  STATION_TYPE,
  SCHOOL_BUILDING,
  PM_SHRI_PHASE,
  INTERNET_PLAN,
  INTERNET_CONNECTIVITY,
  INTERNET_TYPE,
  NCC_GROUP,
  SPORT_CATEGORY,
  SPORT_TYPE,
  SPORT_TITLE,
  SPORT_QUALITY,
  SPORT_MEASURE_UNIT,
  LAB_FUNDED_BY,
} from '../constants/lookupMaps';

// ─── Lookup map for dynamic filter config generation ─────────

const FILTER_LOOKUP_MAPS: Record<string, any> = {
  ownership: OWNERSHIP_TYPE,
  ownershiptype: OWNERSHIP_TYPE,
  locationtype: LOCATION_TYPE,
  location: LOCATION_TYPE,
  sponsoredby: SPONSORED_BY,
  stationtype: STATION_TYPE,
  schoolbuilding: SCHOOL_BUILDING,
  buildingtype: SCHOOL_BUILDING,
  ispmshree: PM_SHRI_PHASE,
  pmshri: PM_SHRI_PHASE,
  internetplan: INTERNET_PLAN,
  internetconnectivity: INTERNET_CONNECTIVITY,
  internettype: INTERNET_TYPE,
  nccgroup: NCC_GROUP,
  sportcategory: SPORT_CATEGORY,
  category: SPORT_CATEGORY,
  type: SPORT_TYPE,
  sporttype: SPORT_TITLE,
  sporttitle: SPORT_TITLE,
  sportstitle: SPORT_TITLE,
  quality: SPORT_QUALITY,
  measurementunit: SPORT_MEASURE_UNIT,
  fundedby: LAB_FUNDED_BY,
  funded: LAB_FUNDED_BY,
};

const DYNAMIC_DROPDOWN_FIELDS = [
  'sporttype', 'sportType', 'sport_type',
  'sportstitle', 'sportsTitle', 'sports_title', 'Sports Title',
  'sporttitle', 'sportTitle', 'sport_title',
  'gamename', 'gameName', 'game_name',
  'sportsname', 'sportsName', 'sports_name',
  'sportname', 'sportName', 'sport_name',
  'title', 'Title',
];

// ─── Hook Params ─────────────────────────────────────────────

interface UseKPIDataParams {
  state: string;
  region: string;
  category: string;
  kpi: string;
  selectedInfraItems: string[];
  globalFilters: FilterCondition[];
  activeQuickFilters: string[];
  setFilterConfigs: (v: FilterConfig[]) => void;
}

// ─── Hook Return ─────────────────────────────────────────────

export interface UseKPIDataReturn {
  // Dropdown data
  statesList: StateListItem[];
  statesLoading: boolean;
  statesError: string | null;
  regionsList: RegionListItem[];
  regionsLoading: boolean;
  regionsError: string | null;
  categoryList: CategoryListItem[];
  kpiList: CategoryListItem[];
  kpiLoading: boolean;
  kpiError: string | null;

  // KPI data
  kpiDataItems: KPIDataItem[];
  kpiDataLoading: boolean;
  kpiDataError: string | null;
  aggregatedStateData: AggregatedStateData[];
  aggregatedRegionData: AggregatedRegionData[];

  // Row expansion
  expandedRows: Set<number>;
  setExpandedRows: (v: Set<number>) => void;
  expandedDefaultRows: Set<number>;
  setExpandedDefaultRows: (v: Set<number>) => void;

  // Map & counts
  allSchoolCoords: any[];
  selectedAreaCounts: { totalKVs: number; pmShree: number };
  totalSchools: number | null;

  // Derived
  categoryConfig: CategoryConfig;
  isInfra: boolean;
  filteredStatesList: StateListItem[];
  filteredAggregatedData: AggregatedStateData[];
  infraPivotedSchools: any[];
  infraDefaultColumns: string[];
  kpiSchoolCoordinates: any[];
  allKpiSchools: any[];
  filteredAllKpiSchools: any[];
  visibleGroups: Array<{ item: KPIDataItem; filteredSchools: any[]; index: number }>;
}

// ═══════════════════════════════════════════════════════════════
// HOOK
// ═══════════════════════════════════════════════════════════════

export function useKPIData(params: UseKPIDataParams): UseKPIDataReturn {
  const {
    state,
    region,
    category,
    kpi,
    selectedInfraItems,
    globalFilters,
    activeQuickFilters,
    setFilterConfigs,
  } = params;

  // ── Dropdown lists ─────────────────────────────────────────
  const [statesList, setStatesList] = useState<StateListItem[]>([]);
  const [statesLoading, setStatesLoading] = useState(false);
  const [statesError, setStatesError] = useState<string | null>(null);

  const [regionsList, setRegionsList] = useState<RegionListItem[]>([]);
  const [regionsLoading, setRegionsLoading] = useState(false);
  const [regionsError, setRegionsError] = useState<string | null>(null);

  const [categoryList, setCategoryList] = useState<CategoryListItem[]>([]);
  const [kpiList, setKpiList] = useState<CategoryListItem[]>([]);
  const [kpiLoading, setKpiLoading] = useState(false);
  const [kpiError, setKpiError] = useState<string | null>(null);

  // ── KPI data ───────────────────────────────────────────────
  const [kpiDataItems, setKpiDataItems] = useState<KPIDataItem[]>([]);
  const [kpiDataLoading, setKpiDataLoading] = useState(false);
  const [kpiDataError, setKpiDataError] = useState<string | null>(null);
  const [aggregatedStateData, setAggregatedStateData] = useState<AggregatedStateData[]>([]);
  const [aggregatedRegionData, setAggregatedRegionData] = useState<AggregatedRegionData[]>([]);

  // ── Expansion state ────────────────────────────────────────
  const [expandedRows, setExpandedRows] = useState<Set<number>>(new Set());
  const [expandedDefaultRows, setExpandedDefaultRows] = useState<Set<number>>(new Set());

  // ── Map data ───────────────────────────────────────────────
  const [allSchoolCoords, setAllSchoolCoords] = useState<any[]>([]);
  const [selectedAreaCounts, setSelectedAreaCounts] = useState({ totalKVs: 0, pmShree: 0 });
  const [totalSchools, setTotalSchools] = useState<number | null>(null);

  // ── Category config ────────────────────────────────────────
  const categoryConfig = useMemo(
    () => getCategoryConfig(category),
    [category],
  );

  // ── Is Infrastructure category ─────────────────────────────
  const isInfra = useMemo(() => {
    // If the config explicitly declares pivotData / multiSelect, honour it
    if (categoryConfig.pivotData || categoryConfig.kpi?.multiSelect) return true;
    if (!category) return false;
    const categoryLabel = categoryList.find(c => c.key === category)?.label || '';
    if (isInfrastructureCategory(category) || isInfrastructureCategory(categoryLabel)) return true;
    if (kpi === 'infra_multi') return true;
    return false;
  }, [category, categoryList, kpi, categoryConfig]);

  const getPmShreeValue = (school: any): number => {
    const raw =
      school?.isPMShree ??
      school?.is_pm_shree ??
      school?.schoolDetails?.isPMShree ??
      school?.schoolDetails?.is_pm_shree ??
      0;
    const n = Number(raw);
    return Number.isFinite(n) ? n : 0;
  };

  // ══════════════════════════════════════════════════════════
  // INITIAL DATA FETCHES (run once)
  // ══════════════════════════════════════════════════════════

  useEffect(() => {
    getTotalSchools()
      .then(data => setTotalSchools(typeof data === 'number' ? data : data?.total || null))
      .catch(() => setTotalSchools(null));
  }, []);

  useEffect(() => {
    let mounted = true;
    getAllSchoolCoordinates()
      .then((data: any[]) => { if (mounted && Array.isArray(data)) setAllSchoolCoords(data); })
      .catch(() => setAllSchoolCoords([]));
    return () => { mounted = false; };
  }, []);

  useEffect(() => {
    setStatesLoading(true);
    getAllStates()
      .then(data => {
        setStatesList(
          Array.isArray(data)
            ? data.map((s: any) => ({ stateID: s.stateID, stateName: s.stateName, schoolCount: 0, kvisDetails: [] }))
            : []
        );
        setStatesError(null);
      })
      .catch(() => { setStatesList([]); setStatesError('Failed to load states'); })
      .finally(() => setStatesLoading(false));
  }, []);

  useEffect(() => {
    setRegionsLoading(true);
    getAllRegions()
      .then(data => { setRegionsList(data); setRegionsError(null); })
      .catch(() => { setRegionsList([]); setRegionsError('Failed to load regions'); })
      .finally(() => setRegionsLoading(false));
  }, []);

  useEffect(() => {
    getAllCategories()
      .then(data => setCategoryList(Array.isArray(data) ? data : []))
      .catch(() => setCategoryList([]));
  }, []);

  // ══════════════════════════════════════════════════════════
  // KPI OPTIONS — fetch when category changes
  // ══════════════════════════════════════════════════════════

  useEffect(() => {
    if (!category || typeof category !== 'string') { setKpiList([]); return; }

    // If the category config provides static KPI options, use them directly
    if (categoryConfig.kpi?.staticOptions) {
      setKpiList(categoryConfig.kpi.staticOptions);
      setKpiError(null);
      setKpiLoading(false);
      return;
    }

    // Legacy fallback for infra detection without a dedicated config
    if (isInfra) {
      setKpiList(INFRA_KPI_OPTIONS);
      setKpiError(null);
      setKpiLoading(false);
      return;
    }

    setKpiLoading(true);
    getKPIsByCategory(category)
      .then((data: CategoryListItem[]) => { setKpiList(Array.isArray(data) ? data : []); setKpiError(null); })
      .catch(() => { setKpiList([]); setKpiError('Failed to load KPIs'); })
      .finally(() => setKpiLoading(false));
  }, [category, categoryList, isInfra, categoryConfig]);

  // ══════════════════════════════════════════════════════════
  // RESET expansion & filters on category/kpi change
  // ══════════════════════════════════════════════════════════

  useEffect(() => {
    setExpandedRows(new Set());
    setExpandedDefaultRows(new Set());
  }, [globalFilters]);

  // ══════════════════════════════════════════════════════════
  // KPI DATA FETCH + POST-PROCESSING
  // ══════════════════════════════════════════════════════════

  useEffect(() => {
    let mounted = true;

    const processKpiItems = (rawItems: any[]) => {
      let kpiItems: KPIDataItem[] = rawItems;

      // — State / region filtering —
      if (state || region) {
        kpiItems = kpiItems
          .filter(item => {
            if (state) {
              if (item.stateName === state) return true;
              return item.kvs_details?.some((s: any) => s.stateName === state || s.schoolDetails?.stateName === state);
            }
            if (region) {
              if (item.roName === region) return true;
              return item.kvs_details?.some((s: any) => s.roName === region || s.schoolDetails?.roName === region);
            }
            return true;
          })
          .map(item => {
            if (!Array.isArray(item.kvs_details)) return item;
            let filtered = item.kvs_details;
            if (state) filtered = filtered.filter((s: any) => s.stateName === state || s.schoolDetails?.stateName === state);
            if (region) filtered = filtered.filter((s: any) => s.roName === region || s.schoolDetails?.roName === region);
            return { ...item, kvs_details: filtered, count: filtered.length, 'School Count': filtered.length };
          })
          .filter(item => item.count > 0);
      }

      // — Binary-flag KPI filtering —
      if (kpi && kpiItems.length > 0) {
        const allKvs = kpiItems.flatMap(item => (Array.isArray(item.kvs_details) ? item.kvs_details : []));
        const vals = allKvs.map(s => String(s[kpi] ?? '').toLowerCase()).filter(v => v !== '');
        const unique = Array.from(new Set(vals));
        if (unique.length > 0 && unique.every(v => ['0', '1', 'yes', 'no', 'true', 'false'].includes(v))) {
          kpiItems = kpiItems
            .map(item => ({
              ...item,
              kvs_details: Array.isArray(item.kvs_details)
                ? item.kvs_details.filter(s => {
                    const v = s[kpi];
                    return v === 1 || v === '1' || v === true || String(v).toLowerCase() === 'yes' || String(v).toLowerCase() === 'true';
                  })
                : item.kvs_details,
            }))
            .filter(item => item.kvs_details?.length > 0);
        }
      }

      // — Funded By: exclude value 0 —
      if (kpi && (kpi.toLowerCase() === 'fundedby' || kpi.toLowerCase() === 'funded by')) {
        kpiItems = kpiItems
          .map(item => ({
            ...item,
            kvs_details: Array.isArray(item.kvs_details)
              ? item.kvs_details.filter(s => {
                  const v = s[kpi] || s.fundedBy || s.funded_by || s.FundedBy;
                  return v !== 0 && v !== '0' && v != null && v !== '';
                })
              : item.kvs_details,
          }))
          .filter(item => item.kvs_details?.length > 0);
      }

      setKpiDataItems(kpiItems);

      // — Generate filter configs dynamically —
      buildFilterConfigs(kpiItems);

      // — Aggregate for map / chart —
      const aggregated: AggregatedStateData[] = kpiItems.map((item, i) => {
        const detailsCount = Array.isArray(item.kvs_details) ? item.kvs_details.length : 0;
        let count = item.count || item['School Count'] || 0;
        if (count === 0 && detailsCount > 0) count = detailsCount;

        let label = item.roName || item.stateName || item.districtName || item.city || '';
        if (!label) {
          const key = Object.keys(item).find(k => !['count', 'School Count', 'kvs_details', 'roID', 'stateID', 'districtID'].includes(k));
          label = key ? item[key] : `Item ${i}`;
        }
        return {
          stateID: item.roID || item.stateID || item.districtID || i,
          stateName: String(label),
          schoolCount: count,
          value: count,
        };
      });

      setAggregatedStateData(aggregated);
      setAggregatedRegionData([]);
    };

    // — Build filter configs from first school record —
    const buildFilterConfigs = (kpiItems: KPIDataItem[]) => {
      if (kpiItems.length === 0 || !kpiItems[0].kvs_details?.length) return;

      const firstSchool = kpiItems[0].kvs_details[0];
      const allSchools = kpiItems.flatMap(item => (Array.isArray(item.kvs_details) ? item.kvs_details : []));
      const configs: FilterConfig[] = [];

      /**
       * Distinct values for a field, for the filter dropdowns.
       *
       * Source data spells some values inconsistently ('ODISHA' vs 'Odisha'),
       * which listed the same state twice. Group case-insensitively and show
       * the spelling that occurs most often, preferring a mixed-case variant
       * over an all-caps one when they tie. Collapsing to a single entry is
       * safe because filter matching is already case-insensitive
       * (see applyFilters in utils/filterUtils), so the one entry still
       * selects records stored under either spelling.
       */
      const collectUnique = (field: string) => {
        const groups = new Map<string, Map<string, number>>();
        allSchools.forEach((s: any) => {
          const v = s[field];
          if (v == null || v === '') return;
          const raw = String(v).trim();
          if (!raw) return;
          const key = raw.toUpperCase();
          const variants = groups.get(key) ?? new Map<string, number>();
          variants.set(raw, (variants.get(raw) ?? 0) + 1);
          groups.set(key, variants);
        });

        return Array.from(groups.values())
          .map(variants => {
            let best = '';
            let bestCount = -1;
            variants.forEach((count, spelling) => {
              const beatsOnCount = count > bestCount;
              const tieButBetterCase =
                count === bestCount &&
                best === best.toUpperCase() &&
                spelling !== spelling.toUpperCase();
              if (beatsOnCount || tieButBetterCase) {
                best = spelling;
                bestCount = count;
              }
            });
            return best;
          })
          .sort((a, b) => a.localeCompare(b, undefined, { sensitivity: 'base' }))
          .map(v => ({ value: v, label: v }));
      };

      const extract = (obj: any, prefix = '') => {
        Object.entries(obj).forEach(([key, value]) => {
          const fullKey = prefix ? `${prefix}.${key}` : key;
          if (key === 'schoolID' || key === 'kvs_details') return;

          if (value && typeof value === 'object' && !Array.isArray(value)) {
            extract(value, fullKey);
            return;
          }

          let type: 'text' | 'number' | 'select' | 'boolean' = 'text';
          let options: Array<{ value: any; label: string }> | undefined;
          if (typeof value === 'number') type = 'number';
          else if (typeof value === 'boolean') type = 'boolean';

          const normalizedKey = key.replace(/\s+/g, '').replace(/\./g, '').toLowerCase();
          const lookupMap = FILTER_LOOKUP_MAPS[normalizedKey];

          if (lookupMap) {
            type = 'select';
            options = Object.entries(lookupMap).map(([v, l]) => ({ value: v, label: String(l) }));
          } else if (DYNAMIC_DROPDOWN_FIELDS.some(f => f.toLowerCase().replace(/\s+/g, '') === normalizedKey)) {
            const unique = collectUnique(key);
            if (unique.length > 0) {
              const norm = unique.map(o => String(o.value).toLowerCase());
              const isBin = norm.length === 2 && norm.every(v => v === '0' || v === '1');
              const isYN = norm.length === 2 && norm.every(v => v === 'yes' || v === 'no');
              if (isBin || isYN) {
                type = 'boolean';
                options = [{ value: isBin ? '1' : 'yes', label: 'Yes' }, { value: isBin ? '0' : 'no', label: 'No' }];
              } else {
                type = 'select';
                options = unique;
              }
            }
          } else if (typeof value === 'string' && value !== '') {
            const unique = collectUnique(key);
            if (unique.length > 0 && unique.length <= 50) {
              const norm = unique.map(o => String(o.value).toLowerCase());
              const isBin = norm.length === 2 && norm.every(v => v === '0' || v === '1');
              const isYN = norm.length === 2 && norm.every(v => v === 'yes' || v === 'no');
              if (isBin || isYN) {
                type = 'boolean';
                options = [{ value: isBin ? '1' : 'yes', label: 'Yes' }, { value: isBin ? '0' : 'no', label: 'No' }];
              } else {
                type = 'select';
                options = unique;
              }
            }
          }

          const label = key.replace(/([A-Z])/g, ' $1').replace(/^./, s => s.toUpperCase()).trim();
          configs.push({ field: fullKey, label, type, options });
        });
      };

      extract(firstSchool);
      const filtered = configs.filter(c => {
        const f = c.field.toLowerCase();
        return !f.includes('id') && !f.includes('latitude') && !f.includes('longitude');
      });

      // Add infra quantity columns as filter options
      if (isInfra && selectedInfraItems.length > 0) {
        const infraConfigs: FilterConfig[] = selectedInfraItems.map(id => {
          const label = INFRA_KPI_OPTIONS.find(o => o.key === id)?.label || `Infra ${id}`;
          return { field: `infra_${id}`, label: `${label} (Qty)`, type: 'number' as const, isPrimary: true };
        });
        setFilterConfigs([...infraConfigs, ...filtered]);
      } else {
        setFilterConfigs(filtered);
      }
    };

    // — Fetch logic —
    const apiKpiKey = categoryConfig.kpi?.apiKpiKey
      ? categoryConfig.kpi.apiKpiKey
      : (isInfra ? 'quantity' : kpi);
    const cacheKey = isInfra
      ? `kpiData:${category}:${apiKpiKey}:${state || ''}:${region || ''}`
      : `kpiData:${category}:${kpi}:${state || ''}:${region || ''}`;

    // Normalise whatever the API returns into a flat kpi_data array
    const extractKpiData = (raw: any): any[] | null => {
      if (!raw) return null;
      if (Array.isArray(raw)) return raw;                        // direct array
      if (Array.isArray(raw.kpi_data)) return raw.kpi_data;      // { kpi_data: [] }
      if (Array.isArray(raw.data)) return raw.data;               // { data: [] }
      if (Array.isArray(raw.result)) return raw.result;           // { result: [] }
      if (Array.isArray(raw.records)) return raw.records;         // { records: [] }
      return null;
    };

    const fetchData = async () => {
      // For infra, kpi = 'infra_multi' once an item is selected; for others require explicit kpi
      if (!category) return;
      if (isInfra && selectedInfraItems.length === 0) return;
      if (!isInfra && !kpi) return;

      console.log('[KPIData] fetchData CALLING API', { category, apiKpiKey });
      setKpiDataLoading(true);
      setKpiDataError(null);
      setExpandedRows(new Set());

      // Try cache
      try {
        const cached = sessionStorage.getItem(cacheKey);
        if (cached) {
          const parsed = JSON.parse(cached);
          const arr = extractKpiData(parsed);
          if (arr && arr.length > 0) {
            if (mounted) processKpiItems(arr);
            setKpiDataLoading(false);
            return;
          }
        }
      } catch { /* cache error - proceed to fetch */ }

      try {
        const data: any = await getKPIData(category, apiKpiKey);
        const arr = extractKpiData(data);
        if (arr && arr.length > 0) {
          try { sessionStorage.setItem(cacheKey, JSON.stringify({ kpi_data: arr })); } catch { /* ignore */ }
          if (mounted) processKpiItems(arr);
        } else {
          // API responded but returned empty / unexpected format
          setKpiDataItems([]);
          setAggregatedStateData([]);
          setAggregatedRegionData([]);
          const label = categoryList.find(c => c.key === category)?.label || category;
          setKpiDataError(`No data returned for ${label}. The API may not yet support this category.`);
        }
      } catch (err: any) {
        setKpiDataError(err?.message || `Failed to load data for ${category}`);
        setKpiDataItems([]);
        setAggregatedStateData([]);
        setAggregatedRegionData([]);
      } finally {
        if (mounted) setKpiDataLoading(false);
      }
    };

    fetchData();
    return () => { mounted = false; };
  }, [category, kpi, state, region, statesList, isInfra, selectedInfraItems, setFilterConfigs]);

  // ══════════════════════════════════════════════════════════
  // AREA COUNTS (for map sidebar)
  // ══════════════════════════════════════════════════════════

  useEffect(() => {
    const getIsPM = (s: any) => (Number(s?.isPMShree ?? 0) > 0 ? 1 : 0);

    let source: any[] =
      category && kpi && kpiDataItems.length > 0
        ? kpiDataItems.flatMap(item => (Array.isArray(item.kvs_details) ? item.kvs_details : []))
        : allSchoolCoords;

    const byState = (s: any) => (s?.stateName || s?.state || '').toString().trim().toLowerCase() === state.trim().toLowerCase();
    const byRegion = (s: any) => (s?.roName || s?.ROname || s?.region || s?.ro || '').toString().trim().toLowerCase() === region.trim().toLowerCase();

    let filtered = source;
    if (region) filtered = source.filter(byRegion);
    else if (state) filtered = source.filter(byState);

    // Deduplicate by schoolID
    const map = new Map<string | number, any>();
    filtered.forEach((s: any) => {
      const id = s.schoolID || s.school_id || s.kvID || s.id;
      if (id && !map.has(id)) { map.set(id, s); return; }
      if (!id) {
        const key = `${s.schoolName || ''}_${s.stateName || ''}_${s.districtName || ''}`;
        if (!map.has(key)) map.set(key, s);
      }
    });

    const unique = Array.from(map.values());
    setSelectedAreaCounts({ totalKVs: unique.length, pmShree: unique.reduce((a, s) => a + getIsPM(s), 0) });
  }, [state, region, category, kpi, kpiDataItems, allSchoolCoords]);

  // ══════════════════════════════════════════════════════════
  // DERIVED / COMPUTED VALUES (all memoized)
  // ══════════════════════════════════════════════════════════

  const filteredStatesList = useMemo(() => {
    let list = statesList;
    if (!state && !region && globalFilters.length === 0) return statesList;
    if (state) list = list.filter(s => s.stateName === state);

    if (globalFilters.length > 0) {
      return list
        .map(s => {
          const raw = Array.isArray(s.kvisDetails) ? s.kvisDetails : [];
          const filtered = applyFilters(raw, globalFilters);
          return { ...s, kvisDetails: filtered, schoolCount: filtered.length } as typeof s;
        })
        .filter(s => s.kvisDetails && s.kvisDetails.length > 0);
    }
    return list;
  }, [statesList, state, region, globalFilters]);

  // Infrastructure pivoted schools
  const infraPivotedSchools = useMemo(() => {
    if (!isInfra || kpiDataItems.length === 0) return [];

    const stdFilters = globalFilters.filter(f => !f.field.startsWith('infra_'));
    const infraFilters = globalFilters.filter(f => f.field.startsWith('infra_'));

    const allSchools: any[] = [];
    kpiDataItems.forEach(item => {
      if (Array.isArray(item.kvs_details)) {
        allSchools.push(...applyFilters(item.kvs_details, stdFilters));
      }
    });

    let pivoted = pivotInfraData(allSchools, selectedInfraItems.length > 0 ? selectedInfraItems : undefined);
    if (infraFilters.length > 0) pivoted = applyFilters(pivoted, infraFilters);
    return pivoted;
  }, [isInfra, kpiDataItems, globalFilters, selectedInfraItems]);

  const infraDefaultColumns = useMemo(() => {
    if (!isInfra) return [];
    return getInfraDefaultColumns(selectedInfraItems);
  }, [isInfra, selectedInfraItems]);

  // Filtered aggregated data for charts
  const filteredAggregatedData = useMemo(() => {
    const stdFilters = isInfra ? globalFilters.filter(f => !f.field.startsWith('infra_')) : globalFilters;
    const infraFilters = isInfra ? globalFilters.filter(f => f.field.startsWith('infra_')) : [];

    if (stdFilters.length === 0 && infraFilters.length === 0 && (!category || !kpi)) {
      if (isInfra && kpi && kpiDataItems.length > 0) {
        return kpiDataItems
          .map((item, i) => {
            if (!Array.isArray(item.kvs_details)) return { stateID: i, stateName: `Item ${i}`, schoolCount: 0, value: 0 };
            let label = item.roName || item.stateName || item.districtName || item.city || '';
            if (!label) {
              const k = Object.keys(item).find(k => !['count', 'School Count', 'kvs_details', 'roID', 'stateID', 'districtID'].includes(k));
              label = k ? item[k] : `Item ${i}`;
            }
            const qty = item.kvs_details.reduce((s: number, d: any) => s + Number(d.quantity ?? d.Quantity ?? 0), 0);
            return { stateID: item.roID || item.stateID || item.districtID || i, stateName: String(label), schoolCount: qty, value: qty };
          })
          .filter(x => x.schoolCount > 0);
      }
      return aggregatedStateData;
    }

    if (isInfra && infraFilters.length > 0) {
      return kpiDataItems
        .map((item, i) => {
          let label = item.roName || item.stateName || item.districtName || item.city || '';
          if (!label) {
            const k = Object.keys(item).find(k => !['count', 'School Count', 'kvs_details', 'roID', 'stateID', 'districtID'].includes(k));
            label = k ? item[k] : `Item ${i}`;
          }
          const ct = infraPivotedSchools.filter(ps => {
            if (item.roName) return ps.roName === item.roName;
            if (item.stateName) return ps.stateName === item.stateName;
            if (item.districtName) return ps.districtName === item.districtName;
            if (item.city) return ps.city === item.city;
            return true;
          }).length;
          return { stateID: item.roID || item.stateID || item.districtID || i, stateName: String(label), schoolCount: ct, value: ct };
        })
        .filter(x => x.schoolCount > 0);
    }

    return kpiDataItems
      .map((item, i) => {
        if (!Array.isArray(item.kvs_details)) {
          return {
            stateID: item.roID || item.stateID || item.districtID || i,
            stateName: item.roName || item.stateName || item.districtName || item.city || `Item ${i}`,
            schoolCount: 0,
            value: 0,
          };
        }
        const filtered = applyFilters(item.kvs_details, stdFilters);
        let label = item.roName || item.stateName || item.districtName || item.city || '';
        if (!label) {
          const k = Object.keys(item).find(k => !['count', 'School Count', 'kvs_details', 'roID', 'stateID', 'districtID'].includes(k));
          label = k ? item[k] : `Item ${i}`;
        }
        return { stateID: item.roID || item.stateID || item.districtID || i, stateName: String(label), schoolCount: filtered.length, value: filtered.length };
      })
      .filter(x => x.schoolCount > 0);
  }, [aggregatedStateData, kpiDataItems, globalFilters, category, kpi, isInfra, infraPivotedSchools]);

  // KPI school coordinates for map markers
  const kpiSchoolCoordinates = useMemo(() => {
    if (!category || !kpi || kpiDataItems.length === 0) return [];
    const map = new Map<string | number, any>();

    const addCoord = (school: any, fallbackRoName?: string, fallbackStateName?: string) => {
      const id = school.schoolID || school.school_id || school.kvID || null;
      if (id && map.has(id)) return;
      const lat = Number(school.latitude ?? school.schoolDetails?.latitude);
      const lng = Number(school.longitude ?? school.schoolDetails?.longitude);
      if (!Number.isFinite(lat) || !Number.isFinite(lng)) return;
      const coord = {
        schoolID: id,
        schoolName: school.schoolName || school.school_name || '',
        latitude: lat,
        longitude: lng,
        isPMShree: getPmShreeValue(school),
        roName: school.roName || fallbackRoName || school.ro || '',
        stateName: school.stateName || fallbackStateName || '',
        districtName: school.districtName || school.district || '',
      };
      if (id) { map.set(id, coord); }
      else {
        const key = `${coord.schoolName}_${coord.stateName}_${coord.districtName}`;
        if (!map.has(key)) map.set(key, coord);
      }
    };

    const infraFilters = isInfra ? globalFilters.filter(f => f.field.startsWith('infra_')) : [];

    if (isInfra && infraFilters.length > 0) {
      // Infra filters operate on pivoted fields (infra_*) which don't exist on raw coordinate
      // objects. Use infraPivotedSchools (already correctly filtered) as the coordinate source.
      infraPivotedSchools.forEach((school: any) => addCoord(school));
    } else {
      // Standard path: apply non-infra filters directly against kvs_details
      const stdFilters = isInfra
        ? globalFilters.filter(f => !f.field.startsWith('infra_'))
        : globalFilters;

      kpiDataItems.forEach(item => {
        if (!Array.isArray(item.kvs_details)) return;
        applyFilters(item.kvs_details, stdFilters).forEach((school: any) =>
          addCoord(school, item.roName, item.stateName)
        );
      });
    }

    let result = Array.from(map.values());

    // Enforce quick filter semantics consistently for map coordinates.
    // PM SHRI means phase/value > 0 (not strictly 1).
    if (activeQuickFilters.includes('pm-shri')) {
      result = result.filter((school: any) => Number(school.isPMShree) > 0);
    } else if (activeQuickFilters.includes('non-pm-shri')) {
      result = result.filter((school: any) => Number(school.isPMShree) <= 0);
    }

    return result;
  }, [category, kpi, kpiDataItems, globalFilters, isInfra, infraPivotedSchools, activeQuickFilters]);

  // All KPI schools (flattened)
  const allKpiSchools = useMemo(
    () => kpiDataItems.flatMap(item => (Array.isArray(item.kvs_details) ? item.kvs_details : [])),
    [kpiDataItems]
  );

  // Filtered all KPI schools
  const filteredAllKpiSchools = useMemo(() => {
    const filters = isInfra ? globalFilters.filter(f => !f.field.startsWith('infra_')) : globalFilters;
    return applyFilters(allKpiSchools, filters);
  }, [allKpiSchools, globalFilters, isInfra]);

  // Visible groups (for tabular tab)
  const visibleGroups = useMemo(() => {
    const filters = isInfra ? globalFilters.filter(f => !f.field.startsWith('infra_')) : globalFilters;
    return kpiDataItems
      .map((item, index) => {
        const raw = Array.isArray(item.kvs_details) ? item.kvs_details : [];
        const filtered = applyFilters(raw, filters);
        return { item, filteredSchools: filtered, index };
      })
      .filter(g => g.filteredSchools.length > 0);
  }, [kpiDataItems, globalFilters, isInfra]);

  // ══════════════════════════════════════════════════════════
  // RETURN
  // ══════════════════════════════════════════════════════════

  return {
    statesList,
    statesLoading,
    statesError,
    regionsList,
    regionsLoading,
    regionsError,
    categoryList,
    kpiList,
    kpiLoading,
    kpiError,

    kpiDataItems,
    kpiDataLoading,
    kpiDataError,
    aggregatedStateData,
    aggregatedRegionData,

    expandedRows,
    setExpandedRows,
    expandedDefaultRows,
    setExpandedDefaultRows,

    allSchoolCoords,
    selectedAreaCounts,
    totalSchools,

    categoryConfig,
    isInfra,
    filteredStatesList,
    filteredAggregatedData,
    infraPivotedSchools,
    infraDefaultColumns,
    kpiSchoolCoordinates,
    allKpiSchools,
    filteredAllKpiSchools,
    visibleGroups,
  };
}
